/**
 * ADPOLY campus and category configuration.
 * Add/remove campuses or categories here — pages resolve everything from the route.
 */
export const ADPOLY_ENTITY = "ADPOLY";

/** Current calendar year for Technology inventory (entity/campus scoped in DB). */
export function getTechnologyYear() {
  return String(new Date().getFullYear());
}

/** Filter value for showing records across all years. */
export const TECHNOLOGY_YEAR_FILTER_ALL = "";

/** Filter value for showing records across all semesters. */
export const ACCOMMODATION_SEMESTER_FILTER_ALL = "";

export function buildAccommodationSemesterLists(apiSemesters) {
  var semesters = apiSemesters && apiSemesters.length ? apiSemesters.slice() : [];

  semesters.sort(function (a, b) {
    return String(b).localeCompare(String(a));
  });

  var latestSemester = semesters.length ? semesters[0] : "";

  var filterSemesters = [{ value: ACCOMMODATION_SEMESTER_FILTER_ALL, label: "All" }];
  for (var i = 0; i < semesters.length; i++) {
    filterSemesters.push({ value: semesters[i], label: semesters[i] });
  }

  return {
    currentSemester: latestSemester,
    latestSemester: latestSemester,
    formSemesters: semesters,
    filterSemesters: filterSemesters,
  };
}

/**
 * Build filter + form year option lists from API distinct years.
 * Filter list includes "All" first; default selection remains current year.
 */
export function buildTechnologyYearLists(apiYears) {
  var currentYear = getTechnologyYear();
  var years = apiYears && apiYears.length ? apiYears.slice() : [];

  if (years.indexOf(currentYear) === -1) {
    years.unshift(currentYear);
  }

  years.sort(function (a, b) {
    return String(b).localeCompare(String(a));
  });

  var filterYears = [{ value: TECHNOLOGY_YEAR_FILTER_ALL, label: "All" }];
  for (var i = 0; i < years.length; i++) {
    filterYears.push({ value: years[i], label: years[i] });
  }

  return {
    currentYear: currentYear,
    formYears: years,
    filterYears: filterYears,
  };
}

export const ADPOLY_CAMPUSES = {
  "abu-dhabi": {
    key: "abu-dhabi",
    title: "Abu Dhabi",
    label: "ADPOLY Abu Dhabi",
    campusCode: "AD",
    permission: "adpoly-abu-dhabi-index",
    campusPermission: "adpoly-abu-dhabi-index",
    categories: [
      {
        key: "people",
        title: "People",
        icon: ["fas", "users"],
        bgClass: "bg-blue-50",
        borderClass: "border-blue-200",
        iconClass: "text-blue-600",
      },
      {
        key: "technology",
        title: "Technology",
        icon: ["fas", "microchip"],
        bgClass: "bg-purple-50",
        borderClass: "border-purple-200",
        iconClass: "text-purple-600",
      },
      {
        key: "process",
        title: "Process",
        icon: ["fas", "cogs"],
        bgClass: "bg-green-50",
        borderClass: "border-green-200",
        iconClass: "text-green-600",
      },
      {
        key: "facility",
        title: "Facility",
        icon: ["fas", "building"],
        bgClass: "bg-yellow-50",
        borderClass: "border-yellow-200",
        iconClass: "text-yellow-600",
      },
      {
        key: "students",
        title: "Students",
        icon: ["fas", "user"],
        bgClass: "bg-pink-50",
        borderClass: "border-pink-200",
        iconClass: "text-pink-600",
      },
      {
        key: "library",
        title: "Library",
        icon: ["fas", "book"],
        bgClass: "bg-indigo-50",
        borderClass: "border-indigo-200",
        iconClass: "text-indigo-600",
      },
      {
        key: "accommodation",
        title: "Accommodation",
        icon: ["fas", "bed"],
        bgClass: "bg-orange-50",
        borderClass: "border-orange-200",
        iconClass: "text-orange-600",
      },
    ],
  },
  "al-ain": {
    key: "al-ain",
    title: "Al Ain",
    label: "ADPOLY Al Ain",
    campusCode: "AA",
    permission: "adpoly-al-ain-index",
    campusPermission: "adpoly-al-ain-index",
    categories: [
      {
        key: "people",
        title: "People",
        icon: ["fas", "users"],
        bgClass: "bg-blue-50",
        borderClass: "border-blue-200",
        iconClass: "text-blue-600",
      },
      {
        key: "technology",
        title: "Technology",
        icon: ["fas", "microchip"],
        bgClass: "bg-purple-50",
        borderClass: "border-purple-200",
        iconClass: "text-purple-600",
      },
      {
        key: "process",
        title: "Process",
        icon: ["fas", "cogs"],
        bgClass: "bg-green-50",
        borderClass: "border-green-200",
        iconClass: "text-green-600",
      },
      {
        key: "facility",
        title: "Facility",
        icon: ["fas", "building"],
        bgClass: "bg-yellow-50",
        borderClass: "border-yellow-200",
        iconClass: "text-yellow-600",
      },
      {
        key: "students",
        title: "Students",
        icon: ["fas", "user"],
        bgClass: "bg-pink-50",
        borderClass: "border-pink-200",
        iconClass: "text-pink-600",
      },
      {
        key: "library",
        title: "Library",
        icon: ["fas", "book"],
        bgClass: "bg-indigo-50",
        borderClass: "border-indigo-200",
        iconClass: "text-indigo-600",
      },
      {
        key: "accommodation",
        title: "Accommodation",
        icon: ["fas", "bed"],
        bgClass: "bg-orange-50",
        borderClass: "border-orange-200",
        iconClass: "text-orange-600",
      },
    ],
  },
};

export function getCampusConfig(campusKey) {
  if (!campusKey) return null;
  return ADPOLY_CAMPUSES[campusKey.toLowerCase()] || null;
}

export function isValidCampus(campusKey) {
  return !!getCampusConfig(campusKey);
}

export function getCategoryConfig(campusKey, categoryKey) {
  const campus = getCampusConfig(campusKey);
  if (!campus || !categoryKey) return null;
  const key = categoryKey.toLowerCase();
  return campus.categories.find(function (item) {
    return item.key === key;
  }) || null;
}

export function isValidCategory(campusKey, categoryKey) {
  return !!getCategoryConfig(campusKey, categoryKey);
}

/**
 * People sub-pages fed by the faculties table (admin / academic / academic_support).
 * Route keys: admins, academics, academic-support
 */
export const ADPOLY_PEOPLE_TYPES = {
  admins: {
    key: "admins",
    title: "Admins",
    titleSingular: "Admin",
    facultyType: "admin",
    icon: ["fas", "user-shield"],
    iconClass: "text-blue-600",
    chartLabel: "Admins by Department",
  },
  academics: {
    key: "academics",
    title: "Academics",
    titleSingular: "Academic",
    facultyType: "academic",
    icon: ["fas", "chalkboard-teacher"],
    iconClass: "text-green-600",
    chartLabel: "Academics by Department",
  },
  "academic-support": {
    key: "academic-support",
    title: "Academic Support",
    titleSingular: "Academic Support",
    facultyType: "academic_support",
    icon: ["fas", "handshake"],
    iconClass: "text-teal-600",
    chartLabel: "Academic Support by Department",
  },
};

export function getPeopleTypeConfig(peopleTypeKey) {
  if (!peopleTypeKey) return null;
  return ADPOLY_PEOPLE_TYPES[peopleTypeKey.toLowerCase()] || null;
}

export function isValidPeopleType(peopleTypeKey) {
  return !!getPeopleTypeConfig(peopleTypeKey);
}
